import React from "react";

/**
 * AgentBlueprint — cinematic 8-stage horizontal flow describing HOW the
 * owner's sub-agents are built. Matches the user's reference reel "How
 * to Build an AI Agent" by ayushukla.techno, but skinned to RAW-DATA:
 *   1. Define Purpose & Scope
 *   2. System Prompt Design
 *   3. Choose LLM
 *   4. Tools & Integrations
 *   5. Memory Systems
 *   6. Orchestration
 *   7. User Interface
 *   8. Testing & Evals
 *
 * Voice trigger: "show me how I've made an agent / how my agents work".
 * Renders as a stage panel (stage type `agent_blueprint`).
 */

const STAGES = [
  {
    n: 1, title: "Define Purpose & Scope", colour: "#7CFFB7",
    chips: ["Use case", "User needs", "Success criteria", "Constraints"],
    desc: "What problem this agent solves, who it serves, when success is reached.",
  },
  {
    n: 2, title: "System Prompt Design", colour: "#A78BFA",
    chips: ["Goals", "Role / Persona", "Instructions", "Guardrails"],
    desc: "The 'soul' of the agent — written instructions that frame every reply.",
  },
  {
    n: 3, title: "Choose LLM", colour: "#FFC555",
    chips: ["Base model", "Parameters", "Context window", "Cost / latency"],
    desc: "GPT-5.2 / Claude Sonnet 4.5 / Gemini 3 Pro — picked per agent's task.",
  },
  {
    n: 4, title: "Tools & Integrations", colour: "#FF7A7A",
    chips: ["APIs (web)", "Databases & storage", "AI tools & services", "Custom functions"],
    desc: "What this agent can DO — call APIs, read DBs, post socials, send email.",
  },
  {
    n: 5, title: "Memory Systems", colour: "#9AE6FF",
    chips: ["Episodic", "Semantic", "Vector store", "SQL / structured", "File storage"],
    desc: "How the agent REMEMBERS — short-term context + long-term retrieval.",
  },
  {
    n: 6, title: "Orchestration", colour: "#FF7FB1",
    chips: ["Workflows / flows", "Triggers", "Parameters", "Message queues", "Agent routing", "Error handling"],
    desc: "How agents chain together (Scout → Eva → Dobby) and run on cron schedules.",
  },
  {
    n: 7, title: "User Interface", colour: "#7CFFB7",
    chips: ["Chat interface", "Web app", "API endpoint", "Slack / Discord bot"],
    desc: "How the OWNER talks to the agent — voice command, Studio panel, scheduled run.",
  },
  {
    n: 8, title: "Testing & Evals", colour: "#A78BFA",
    chips: ["Unit tests", "Latency testing", "Quality metrics", "Iterate & improve"],
    desc: "Verifying the agent does its job well — measured, not guessed.",
  },
];

export default function AgentBlueprint({ stack }) {
  return (
    <div data-testid="agent-blueprint" style={{ color: "var(--rc-text)", padding: 6 }}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="font-display text-2xl rc-glow-text tracking-widest">// HOW I BUILD AN AGENT</div>
          <div className="text-[10px]" style={{ color: "var(--rc-text-dim)", letterSpacing: 2 }}>
            8 STAGES · END-TO-END BLUEPRINT
          </div>
        </div>
        <div className="text-[10px] rc-label">
          {STAGES.length} STAGES · {STAGES.reduce((s, x) => s + x.chips.length, 0)} COMPONENTS
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 12 }}>
        {STAGES.map((s, i) => (
          <div
            key={s.n}
            data-testid={`blueprint-stage-${s.n}`}
            style={{
              border: `1px solid ${s.colour}55`,
              background: `linear-gradient(180deg, ${s.colour}11 0%, rgba(0,0,0,0.4) 100%)`,
              borderRadius: 6,
              padding: 14,
              position: "relative",
              animation: `blueprint-in 0.5s cubic-bezier(0.16, 1, 0.3, 1) ${i * 0.08}s both`,
            }}
          >
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 8 }}>
              <span style={{ color: s.colour, fontSize: 18, fontFamily: "var(--rc-font-display)", fontWeight: 700 }}>{String(s.n).padStart(2, "0")}.</span>
              <span style={{ color: s.colour, fontSize: 14, fontFamily: "var(--rc-font-display)", letterSpacing: 1 }}>{s.title.toUpperCase()}</span>
            </div>
            <div style={{ fontSize: 11, color: "var(--rc-text-dim)", lineHeight: 1.5, marginBottom: 10 }}>
              {s.desc}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
              {s.chips.map((chip, j) => (
                <span
                  key={j}
                  style={{
                    fontSize: 10, padding: "3px 8px",
                    border: `1px solid ${s.colour}66`,
                    color: s.colour,
                    borderRadius: 100,
                    letterSpacing: 0.5,
                    background: "rgba(0,0,0,0.3)",
                  }}
                >{chip}</span>
              ))}
            </div>
            {/* Arrow to next stage */}
            {i < STAGES.length - 1 && (
              <span style={{ position: "absolute", right: -8, top: "50%", transform: "translateY(-50%)", color: s.colour, fontSize: 14, opacity: 0.4 }}>›</span>
            )}
          </div>
        ))}
      </div>

      {/* Bottom strip: actual brain/agents stack the owner is running */}
      {stack && (
        <div style={{ marginTop: 20, padding: 12, border: "1px solid var(--rc-gold-dim)", borderRadius: 6 }}>
          <div className="rc-label text-[10px] mb-2">// LIVE STACK ON YOUR RAW-DATA</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 8 }}>
            {Object.entries(stack).map(([k, v]) => (
              <div key={k} style={{ fontSize: 11, display: "flex", justifyContent: "space-between", borderLeft: "2px solid var(--rc-gold)", paddingLeft: 8 }}>
                <span style={{ color: "var(--rc-text-dim)", letterSpacing: 1 }}>{k.replace(/_/g, " ").toUpperCase()}</span>
                <span style={{ color: "var(--rc-gold)" }}>{v}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <style>{`
        @keyframes blueprint-in {
          from { opacity: 0; transform: translateY(12px) scale(0.96); }
          to   { opacity: 1; transform: translateY(0)  scale(1); }
        }
      `}</style>
    </div>
  );
}
